import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { compose } from 'redux';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { dehydrate } from 'react-query/hydration';
import Artist from '../../../containers/Artist';
import {
  getChosenProfileRepertoireAction,
  getChosenProfileReviewsAction,
  getChosenProfileBioAction,
  getChosenProfileAgreementsAction,
  getArtistImagesAction,
  getVideoAction,
  setProfileValueAction,
  getArtistBannerImagesAction,
} from '../../../containers/Artist/actions';
import { withTranslation } from '../../../src/i18n';
import withErrorPage from '../../../utils/hocs/withErrorPage';
import * as API from '../../../utils/API';
import {
  PROFILES_ENDPOINT,
  PROFILES_BIO_ENDPOINT,
  MEDIA,
  PROFILE_REPERTOIRE,
} from '../../../constants/endpointConstants';
import {
  fixTabAndSlugId,
  routeValidation,
  testSlugId,
  tabRouteValidation,
  fixTabValues,
} from '../../../utils/routeValidation';
import { getWholeList, getList } from '../../../containers/App/actions';
import getInjectors from '../../../store/reducerInjectors';
import reducer, { key } from '../../../containers/Artist/reducer';
import { useInjectReducer } from '../../../store/injectReducer';
import { getIdFromSlugRoute, prefetchSubfilterOptions } from '../../../utils/common';
import {
  MEDIA_TYPES_IDS,
  MEDIA_TAGS_TYPES_IDS,
  MEDIA_ATTRIBUTES_TYPES_IDS,
  ARTIST_TAB_VALUES,
  ARTIST_TYPE_ID,
  PAGE_NAMES,
} from '../../../constants/consts';
import { formatResponseToEntity, formatListResponseToEntity } from '../../../utils/crudUtils';
import { queryClient } from '../../../utils/queriesUtil';
import { getProductions } from '../../../containers/StubPagePerformances/queries';
import { NotFoundError } from '../../../utils/HttpError';

const i18nNamespaces = ['translation', 'artistSeo'];

const ArtistPage = ({ t, lang, profileId, performances, queryValues }) => {
  useInjectReducer([{ key, reducer }]);
  const router = useRouter();
  const { action } = router.query;
  const activeTab = tabRouteValidation(action, ARTIST_TAB_VALUES);
  const dispatch = useDispatch();

  useEffect(() => {
    if (activeTab === ARTIST_TAB_VALUES.IMAGES) {
      dispatch(
        getList({
          endpoint: MEDIA,
          sagaRoutine: getArtistImagesAction,
          params: {
            queryParams: {
              media_type_id: MEDIA_TYPES_IDS.IMAGE,
              media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PROFILE,
              media_tag_id: profileId,
            },
            pagination: { limit: 50 },
          },
        }),
      );
    }
    if (activeTab === ARTIST_TAB_VALUES.VIDEOS)
      dispatch(
        getList({
          endpoint: MEDIA,
          sagaRoutine: getVideoAction,
          params: {
            queryParams: {
              media_type_id: MEDIA_TYPES_IDS.VIDEO,
              media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PROFILE,
              media_tag_id: profileId,
            },
            pagination: { limit: 50 },
          },
        }),
      );
  }, [activeTab, dispatch, profileId]);
  return <Artist t={t} lang={lang} queryValues={queryValues} performances={performances} activeTab={activeTab} />;
};

ArtistPage.propTypes = {
  t: PropTypes.func.isRequired,
};

async function prefetchProductions(query, pagination, page) {
  const prefetchedPromise = await getProductions(query, pagination, page);
  return [prefetchedPromise, dehydrate(queryClient)];
}

ArtistPage.getInitialProps = async ctx => {
  const { store, query, asPath, pathname, res, req } = ctx;
  const activeTab = tabRouteValidation(query?.action, ARTIST_TAB_VALUES);
  const language = get(req, 'language', 'en');
  getInjectors(store).injectReducer([{ key, reducer }]);
  let profileId = getIdFromSlugRoute(get(query, 'id', ''));
  let statusCode;
  let entity;
  let performances;
  let queryValues;

  try {
    const router = { query, asPath, pathname };
    const numRegExp = /^\d+$/.test(profileId);
    if (!numRegExp && get(query, 'id')) {
      const response = await API.getProfile(ARTIST_TYPE_ID, get(query, 'id'), { limit: 1 });
      const data = formatListResponseToEntity(response);
      entity = get(data, 'entity');
      profileId = get(entity, 'id', '');
      statusCode = routeValidation(
        entity,
        { router, ctx },
        testSlugId,
        fixTabAndSlugId,
        ARTIST_TAB_VALUES,
        fixTabValues,
        301,
      );
      store.dispatch(setProfileValueAction(data));
    } else {
      const response = await API.getOne(PROFILES_ENDPOINT, profileId);
      entity = get(response, 'data');
      statusCode = routeValidation(
        entity,
        { router, ctx },
        testSlugId,
        fixTabAndSlugId,
        ARTIST_TAB_VALUES,
        fixTabValues,
        301,
      );
      store.dispatch(setProfileValueAction(formatResponseToEntity(response)));
    }
    if (profileId) {
      queryValues = { profile_id: profileId, sort: '-year', approved_by: '' };
      [performances] = await prefetchProductions(queryValues, { limit: 10, page: 1 }, PAGE_NAMES.ARTIST);
      store.dispatch(getChosenProfileAgreementsAction({ profileId }));
      store.dispatch(
        getList({
          endpoint: MEDIA,
          sagaRoutine: getArtistBannerImagesAction,
          params: {
            queryParams: {
              media_type_id: MEDIA_TYPES_IDS.IMAGE,
              media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PROFILE,
              media_tag_id: profileId,
              media_attribute_id: MEDIA_ATTRIBUTES_TYPES_IDS.BANNER,
            },
            pagination: { limit: 5 },
          },
        }),
      );

      const isArtist = entity?.profileType?.id === ARTIST_TYPE_ID;
      const shouldShowNotFound = !isArtist || entity?.isHidden === true;
      if (shouldShowNotFound) {
        throw new NotFoundError({ body: 'Artist profile was not found', url: asPath });
      }

      store.dispatch(
        getWholeList({
          endpoint: PROFILE_REPERTOIRE(profileId),
          sagaRoutine: getChosenProfileRepertoireAction,
        }),
      );
      store.dispatch(
        getWholeList({
          endpoint: PROFILES_BIO_ENDPOINT(profileId),
          sagaRoutine: getChosenProfileBioAction,
          params: {},
        }),
      );
      if (activeTab !== ARTIST_TAB_VALUES.IMAGES)
        store.dispatch(
          getList({
            endpoint: MEDIA,
            sagaRoutine: getArtistImagesAction,
            params: {
              queryParams: {
                media_type_id: MEDIA_TYPES_IDS.IMAGE,
                media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PROFILE,
                media_tag_id: profileId,
              },
              pagination: { limit: 5 },
            },
          }),
        );
      if (activeTab !== ARTIST_TAB_VALUES.VIDEOS)
        store.dispatch(
          getList({
            endpoint: MEDIA,
            sagaRoutine: getVideoAction,
            params: {
              queryParams: {
                media_type_id: MEDIA_TYPES_IDS.VIDEO,
                media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PROFILE,
                media_tag_id: profileId,
              },
              pagination: { limit: 5 },
            },
          }),
        );
      store.dispatch(getChosenProfileReviewsAction({ id: profileId, query: {}, pagination: { limit: 5, page: 1 } }));
    }
  } catch (e) {
    statusCode = e?.status || 500;
  } finally {
    if (res && statusCode) {
      res.statusCode = statusCode;
    }
  }

  await prefetchSubfilterOptions({ profile_id: profileId });

  return {
    statusCode,
    lang: language,
    profileId,
    performances,
    queryValues,
    dehydratedState: dehydrate(queryClient),
    namespacesRequired: i18nNamespaces,
  };
};

export default compose(withErrorPage, withTranslation(i18nNamespaces))(ArtistPage);
